// A brand's prepaid credit (Brúnó, 2026-10-08): bought in US dollars through
// Stripe, or added by each paid month of the plan, and spent on the brand's
// campaigns. What a video costs the brand is the creator's fee plus the
// platform fee. The brand sets and sees the creator fee, while the campaign
// budget and credit show the platform fee added on top (Brúnó, 2026-10-14).
// The percentage is a setting (Settings → Brand plans), 20 until the team
// changes it. Pure.
//
// The credit is a prepayment for our own services: it buys nothing from
// anyone else, cannot be sent to another brand, and is not cashed out
// (company-structure/research/2026-10-08-brand-credits-stripe.md). That is
// what keeps it outside money transmission and e-money rules.

/**
 * One purchase, in US cents: the floor ($100, Brúnó 2026-10-08) is above one
 * video with its fee and keeps the card fee near 3%, the ceiling is what one
 * Checkout takes (larger goes by invoice), and above CARD_MAX_CENTS only a
 * bank payment is offered: a card payment that large is a chargeback risk.
 */
export const MIN_TOP_UP_CENTS = 10_000;
export const MAX_TOP_UP_CENTS = 5_000_000;
export const CARD_MAX_CENTS = 500_000;
/** The amounts the portal offers first. */
export const TOP_UP_PRESETS_CENTS: readonly number[] = Object.freeze([10_000, 25_000, 50_000, 100_000]);
export const DEFAULT_PLATFORM_FEE_PERCENT = 20;

/** The setting as a percentage, the default when it is unset or out of range. */
export function platformFeePercent(setting: string | undefined): number {
  const value = Number(String(setting ?? '').trim());
  return String(setting ?? '').trim() !== '' && Number.isInteger(value) && value >= 0 && value <= 100
    ? value : DEFAULT_PLATFORM_FEE_PERCENT;
}

/** The platform fee on a creator's fee, rounded to the cent. */
export function platformFee(creatorCents: number, percent: number): number {
  return Math.round((creatorCents * percent) / 100);
}

/** What the brand pays for work that pays a creator `creatorCents`: fee added on top. */
export function brandPrice(creatorCents: number, percent: number): number {
  return creatorCents + platformFee(creatorCents, percent);
}

/**
 * Legacy campaigns created before 2026-10-14 stored the creator's share
 * inside an all-in brand price. Kept only to read/test those frozen terms;
 * new campaigns store the creator rate directly.
 */
export function creatorShare(brandCents: number, percent: number): number {
  return Math.round((brandCents * 100) / (100 + percent));
}

/** A purchase amount from the portal: whole dollars between the floor and the ceiling, or null. */
export function parseTopUp(value: unknown): number | null {
  const cents = typeof value === 'number' ? value : Number(String(value ?? '').trim());
  if (!Number.isInteger(cents) || cents % 100 !== 0) return null;
  return cents >= MIN_TOP_UP_CENTS && cents <= MAX_TOP_UP_CENTS ? cents : null;
}

/**
 * The part of a card refund or dispute that was credit: Stripe refunds the
 * charged total, which carries any tax on top of the credit, so only the
 * credit's share comes off the balance.
 */
export function creditShare(chargedCents: number, creditCents: number, taxCents: number): number {
  const total = creditCents + taxCents;
  if (total <= 0) return 0;
  return Math.min(creditCents, Math.round((chargedCents * creditCents) / total));
}

/** How often a brand below zero is reminded by email, at most. */
export const DEBT_REMINDER_HOURS = 72;

/**
 * The email to a brand whose bonuses took its credit below zero: what it
 * owes, what stops until it tops up, and that the creators are paid anyway.
 */
export function debtEmail(input: {contact_name: string; balance_cents: number; url: string}): {subject: string; body: string} {
  const owed = `$${(Math.abs(input.balance_cents) / 100).toLocaleString('en-US', {minimumFractionDigits: 2, maximumFractionDigits: 2})}`;
  const first = input.contact_name.trim().split(/\s+/)[0];
  return {
    subject: `Your Earn credit is ${owed} below zero`,
    body: [
      first ? `Hi ${first},` : 'Hi,',
      `Your videos reached view bonuses, and they used more than your free credit: your balance is -${owed}.`,
      'Until you top up, you cannot send a new campaign, and your live campaigns take no new creators. The creators who reached the bonuses are paid all the same.',
      `Add credit here: ${input.url}`,
      'Thank you,\nthe Earn team',
    ].join('\n\n'),
  };
}

const EU_VAT_PREFIX: Readonly<Record<string, string>> = Object.freeze({GR: 'EL'});
const EU_COUNTRIES: ReadonlySet<string> = new Set([
  'AT', 'BE', 'BG', 'HR', 'CY', 'CZ', 'DK', 'EE', 'FI', 'FR', 'DE', 'GR', 'HU', 'IE',
  'IT', 'LV', 'LT', 'LU', 'MT', 'NL', 'PL', 'PT', 'RO', 'SK', 'SI', 'ES', 'SE',
]);

/**
 * The company's EU VAT number as Stripe takes it (`eu_vat`), from the tax id
 * on its profile: with the country's prefix, no spaces or dots. Null outside
 * the EU, or when what was typed cannot be one; then Stripe's own form asks.
 * With a valid one, Stripe Tax charges no VAT across a border (reverse charge).
 */
export function euVatNumber(country: string, taxId: string): string | null {
  const code = country.toUpperCase();
  if (!EU_COUNTRIES.has(code)) return null;
  const prefix = EU_VAT_PREFIX[code] ?? code;
  let value = taxId.toUpperCase().replace(/[\s.\-]/g, '');
  if (!value.startsWith(prefix)) value = `${prefix}${value}`;
  return /^[A-Z]{2}[0-9A-Z]{2,13}$/.test(value) ? value : null;
}

export interface CampaignBudget {
  /** What sending the campaign sets aside: every creator's every video at the highest market's price. */
  total_cents: number;
  /** Every view-bonus tier reached on every video, at the brand's price: charged from the free credit, never set aside. */
  bonus_cents: number;
}

/**
 * What a campaign sets aside of its brand's credit when it is sent for review,
 * in US cents, from the brand's prices (creator fee plus platform fee): each creator's videos
 * at the highest of its markets' prices. View bonuses are not set aside
 * (Brúnó, 2026-10-10): each comes off the free credit when a video reaches
 * its tier, and the balance may go below zero for it. What the work really
 * costs comes off as it is approved; the rest goes back when the campaign
 * ends. A recurring campaign reserves one month. `bonusPerVideoCents` is the
 * sum of the campaign's bonus tiers at the brand's price, already in dollars.
 */
export function campaignBudget(input: {
  highPriceCents: number; videos: number; seats: number; bonusPerVideoCents: number;
}): CampaignBudget {
  const units = Math.max(0, Math.floor(input.videos)) * Math.max(0, Math.floor(input.seats));
  return {
    total_cents: Math.max(0, Math.round(input.highPriceCents)) * units,
    bonus_cents: Math.max(0, Math.round(input.bonusPerVideoCents)) * units,
  };
}
